import { NSLoadingScreen, NSTable } from 'bricks';
import { IExtractedBudgetRow } from 'queries/UploadedProForma/useExtractProFormaValues';
import './BudgetLineItemsTable.scss';

interface IBudgetLineItemsTableProps {
    budgetRows?: IExtractedBudgetRow[];
    isExtracting?: boolean;
}

const formatPeriodValue = (value: string): string => {
    const numericValue = Number(value);
    return value.trim() !== '' && Number.isFinite(numericValue)
        ? numericValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : value;
};

const BudgetLineItemsTable = ({ budgetRows = [], isExtracting = false }: IBudgetLineItemsTableProps) => {
    const lineItemCount = budgetRows.filter(row => !row.noItemsFound).length;

    return (
        <div className="w-50 d-flex flex-column p-3">
            <div className="mb-2">
                <strong>
                    Budget classification
                    {lineItemCount > 0 && ` (${lineItemCount} line items)`}
                </strong>
                <div className="text-muted small">
                    QA view — budget buckets and line items the agent found in the uploaded Excel.
                </div>
            </div>

            {isExtracting ? (
                <div className="d-flex align-items-center justify-content-center flex-grow-1">
                    <NSLoadingScreen shouldLoadAiLogo />
                </div>
            ) : (
                <NSTable bordered size="sm" className="NSTable--no-footer">
                    <thead className="NSTable__thead">
                        <tr className="NSTable__thead__tr">
                            <th className="NSTable__thead__tr__th">Bucket</th>
                            <th className="NSTable__thead__tr__th">Line item</th>
                            <th className="NSTable__thead__tr__th">Total</th>
                            <th className="NSTable__thead__tr__th">Periods</th>
                            <th className="NSTable__thead__tr__th">Sheet</th>
                            <th className="NSTable__thead__tr__th">Cell</th>
                        </tr>
                    </thead>
                    <tbody className="NSTable__tbody">
                        {budgetRows.map(row => (
                            <tr
                                key={`${row.bucket}-${row.name}-${row.sheet}-${row.cell}`}
                                className="NSTable__tbody__tr NSTable__tbody__tr--hoverable"
                            >
                                <td className="NSTable__tbody__tr__td align-middle">{row.bucket}</td>
                                <td className="NSTable__tbody__tr__td align-middle">
                                    {row.noItemsFound
                                        ? <span className="text-muted font-italic">No items found</span>
                                        : row.name || <span className="text-muted">—</span>}
                                </td>
                                <td className="NSTable__tbody__tr__td align-middle">
                                    {row.total ? <code>{row.total}</code> : <span className="text-muted">—</span>}
                                </td>
                                <td className="NSTable__tbody__tr__td align-middle">
                                    {row.periods.length > 0 ? (
                                        <div className="BudgetLineItemsTable__periods">
                                            {row.periods.map(({ period, value }) => (
                                                <div key={period}>
                                                    <code>{`${period}: ${formatPeriodValue(value)}`}</code>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <span className="text-muted">—</span>
                                    )}
                                </td>
                                <td className="NSTable__tbody__tr__td align-middle">
                                    {row.sheet ? row.sheet : <span className="text-muted">—</span>}
                                </td>
                                <td className="NSTable__tbody__tr__td align-middle">
                                    {row.cell ? <code>{row.cell}</code> : <span className="text-muted">—</span>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </NSTable>
            )}
        </div>
    );
};

export default BudgetLineItemsTable;
